import React, { useState } from 'react';
import { ChevronDown, ExternalLink, Dumbbell, Swords, Mountain, Pill, Accessibility } from 'lucide-react';
import { recommendationGroups } from '../data/recommendations';
import { SUPPLEMENT_PARTNERS } from '../data/nutrition';
import { ADAPTIVE_GEAR, ADAPTIVE_GEAR_CATEGORIES, filterAdaptiveGear } from '../data/adaptiveGear';
import { PROSTHESIS_VARIANTS } from '../data/prosthesisVariants';
import { useProfile } from '../context/ProfileContext';
import { useFeatureFlag } from '../context/FeatureFlagsContext';

/**
 * GearVault — single-page directory of every affiliate / gear partner across
 * the app. Lives at the bottom of the Library page. Sections collapsed by
 * default to keep the page calm.
 *
 * Sections (5):
 *   1. Build your gym         (calisthenics)
 *   2. Combat sports          (heavy-bag, jump-rope, BJJ, Muay Thai, Boxing, Karate, Judo, Wrestling, Taekwondo, MMA)
 *   3. Endurance & adventures (walk-run, hiking, cycling-indoor, rowing, swimming, rock-climbing, full-body-cardio)
 *   4. Adaptive gear          (residual-limb, prosthesis accessories, mobility aids, pain management — filterable by variant)
 *   5. Recovery & nutrition   (supplement partners from utils/nutrition)
 *
 * The same affiliate URLs you've already wired up — just centralized so the
 * Display Preferences "Hide this card" hides the pop-up nudge without losing
 * the user's ability to browse partners.
 */

const SECTIONS = [
  {
    key: 'gym',
    title: 'Build your gym',
    icon: Dumbbell,
    blurb: 'Everything you need to train at home — from a doorway pull-up bar to a full power tower.',
    groupKeys: ['calisthenics'],
  },
  {
    key: 'combat',
    title: 'Combat sports',
    icon: Swords,
    blurb: 'Gloves, wraps, gis, mouthguards, shin guards — proper kit for every martial art Enferno tracks.',
    groupKeys: ['heavy-bag', 'jump-rope', 'bjj', 'muay-thai', 'boxing', 'karate', 'judo', 'wrestling', 'taekwondo', 'mma'],
  },
  {
    key: 'recovery',
    title: 'Endurance & Adventures',
    icon: Mountain,
    blurb: 'Shoes, packs, ropes, paddles — gear for the cardio and outdoor activities that round out a strength program.',
    groupKeys: ['walk-run', 'hiking', 'cycling-indoor', 'rowing', 'swimming', 'rock-climbing', 'full-body-cardio'],
  },
  {
    key: 'adaptive',
    title: 'Adaptive gear',
    icon: Accessibility,
    blurb: 'Residual-limb accessories, prosthesis fit aids, single-arm grip attachments, mobility aids, pain management. Filter by your prosthesis variant.',
    custom: 'adaptive',
  },
  {
    key: 'nutrition',
    title: 'Recovery & nutrition',
    icon: Pill,
    blurb: 'Supplements we trust. No magic powders, no fat-burner garbage — just the basics that move the needle.',
    custom: 'supplements',
  },
];

const GearVault = () => {
  const { enabled: vaultEnabled } = useFeatureFlag('vault');
  const [openKey, setOpenKey] = useState(null);
  if (!vaultEnabled) return null;

  return (
    <section className="mt-16" data-testid="gear-vault">
      <div className="border-t border-enf-surface-border pt-12">
        <div className="text-enf-role-info text-xs uppercase tracking-[0.3em] font-semibold mb-2">The Vault</div>
        <h2 className="font-display text-4xl md:text-5xl tracking-wide leading-none mb-2">Gear we trust.</h2>
        <p className="text-enf-text-secondary text-sm max-w-2xl mb-8">
          Every gear and supplement partner Enferno surfaces, in one place. Tap a section to expand.
          Affiliate links — we only list gear we'd use ourselves. A major share of every commission funds IronLimb Foundation's research.
        </p>

        <div className="space-y-3">
          {SECTIONS.map((sec) => {
            const isOpen = openKey === sec.key;
            const Icon = sec.icon;
            return (
              <div key={sec.key} className="bg-enf-surface-deep border border-enf-surface-border rounded-xl overflow-hidden" data-testid={`vault-section-${sec.key}`}>
                <button
                  type="button"
                  onClick={() => setOpenKey(isOpen ? null : sec.key)}
                  className="w-full px-5 py-4 flex items-center justify-between gap-4 hover:bg-enf-surface-mid/60 transition-colors"
                  data-testid={`vault-toggle-${sec.key}`}
                  aria-expanded={isOpen}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-md bg-enf-accent/10 border border-enf-accent-fg/30 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-enf-accent-fg" />
                    </div>
                    <div className="text-left">
                      <div className="font-display text-xl tracking-wide leading-none mb-1">{sec.title}</div>
                      <div className="text-xs text-enf-text-muted">{sec.blurb}</div>
                    </div>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-enf-text-muted flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>

                {isOpen && (
                  <div className="px-5 pb-5 border-t border-enf-surface-border">
                    {sec.custom === 'supplements' ? (
                      <SupplementsGrid />
                    ) : sec.custom === 'adaptive' ? (
                      <AdaptiveGearGrid />
                    ) : (
                      sec.groupKeys.map((gk) => {
                        const group = recommendationGroups[gk];
                        if (!group) return null;
                        return (
                          <div key={gk} className="mt-5">
                            <div className="text-[11px] uppercase tracking-[0.25em] text-enf-role-info font-bold mb-1">{group.title}</div>
                            <div className="text-xs text-enf-text-muted mb-3">{group.blurb}</div>
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                              {group.items.map((it) => (
                                <a
                                  key={`${gk}-${it.id}`}
                                  href={it.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="bg-enf-surface-mid border border-enf-surface-border rounded-lg p-4 hover:border-enf-surface-border transition-colors group"
                                  data-testid={`vault-item-${gk}-${it.id}`}
                                >
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="text-sm font-semibold text-enf-text-primary">{it.name}</div>
                                    <ExternalLink className="w-3 h-3 text-enf-text-muted group-hover:text-enf-role-info transition-colors flex-shrink-0" />
                                  </div>
                                  <div className="text-xs text-enf-text-muted mb-2">{it.priceRange}</div>
                                  <p className="text-xs text-enf-text-secondary leading-relaxed">{it.why}</p>
                                </a>
                              ))}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-6 text-xs text-enf-text-muted leading-relaxed">
          Don't see what you need? Tap <span className="text-enf-role-info">Feedback</span> at the bottom-left — we add partners
          based on tester requests.
        </div>
      </div>
    </section>
  );
};

const SupplementsGrid = () => (
  <div className="mt-5">
    <div className="text-[11px] uppercase tracking-[0.25em] text-enf-role-info font-bold mb-1">Supplement partners</div>
    <div className="text-xs text-enf-text-muted mb-3">Five products we'd recommend to a friend. Skip everything else.</div>
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {SUPPLEMENT_PARTNERS.map((p) => (
        <a key={p.id} href={p.url} target="_blank" rel="noopener noreferrer" className="bg-enf-surface-mid border border-enf-surface-border rounded-lg p-4 hover:border-enf-surface-border transition-colors group" data-testid={`vault-supplement-${p.id}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] uppercase tracking-widest text-enf-role-info font-bold">{p.tag}</span>
            <ExternalLink className="w-3 h-3 text-enf-text-muted group-hover:text-enf-role-info transition-colors" />
          </div>
          <div className="font-display text-lg tracking-wide leading-tight mb-1">{p.name}</div>
          <div className="text-xs text-enf-text-muted mb-2">{p.priceRange}</div>
          <p className="text-xs text-enf-text-secondary leading-relaxed">{p.why}</p>
        </a>
      ))}
    </div>
  </div>
);

// Adaptive gear grid — variant filter chip row + filtered card grid.
// "Mine" defaults to the user's selected prosthesisVariants if any exist;
// "All" clears the filter; tapping a chip toggles that variant in/out.
const AdaptiveGearGrid = () => {
  const { profile } = useProfile();
  const userVariants = profile.prosthesisVariants || [];
  const [filter, setFilter] = useState(userVariants.length > 0 ? userVariants : []);

  const filtered = filterAdaptiveGear(filter);
  const categoryLabel = (catId) =>
    ADAPTIVE_GEAR_CATEGORIES.find((c) => c.id === catId)?.label || catId;

  return (
    <div className="mt-5">
      <div className="text-[11px] uppercase tracking-[0.25em] text-enf-role-info font-bold mb-1">Adaptive gear</div>
      <div className="text-xs text-enf-text-muted mb-3">
        Affiliate partners coming online — list is the curated catalog; URLs go live as deals close.
      </div>

      {/* Filter chip row */}
      <div className="flex flex-wrap items-center gap-1.5 mb-4" data-testid="adaptive-gear-filter-row">
        <button
          type="button"
          onClick={() => setFilter([])}
          data-testid="adaptive-gear-filter-all"
          className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${
            filter.length === 0
              ? 'bg-enf-accent border-enf-accent-fg text-enf-text-primary'
              : 'bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary'
          }`}
        >
          All
        </button>
        {userVariants.length > 0 && (
          <button
            type="button"
            onClick={() => setFilter(userVariants)}
            data-testid="adaptive-gear-filter-mine"
            className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${
              filter.length === userVariants.length && userVariants.every((v) => filter.includes(v))
                ? 'bg-adaptive-accent/20 border-adaptive-accent/40 text-adaptive-accent'
                : 'bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary'
            }`}
          >
            Mine ({userVariants.length})
          </button>
        )}
        <span className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mx-1">·</span>
        {PROSTHESIS_VARIANTS.map((v) => {
          const active = filter.includes(v.id);
          return (
            <button
              key={v.id}
              type="button"
              onClick={() =>
                setFilter((current) =>
                  current.includes(v.id) ? current.filter((id) => id !== v.id) : [...current, v.id]
                )
              }
              data-testid={`adaptive-gear-filter-${v.id}`}
              className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${
                active
                  ? 'bg-adaptive-accent/20 border-adaptive-accent/40 text-adaptive-accent'
                  : 'bg-enf-surface-mid border-enf-surface-border text-enf-text-muted'
              }`}
            >
              {v.label}
            </button>
          );
        })}
      </div>

      {/* Result count */}
      <div className="text-xs text-enf-text-muted mb-3">
        {filtered.length} {filtered.length === 1 ? 'item' : 'items'}
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div className="text-center py-8 text-enf-text-muted text-sm">No matches. Tap All to see the full catalog.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((g) => {
            const hasUrl = !!g.affiliateUrl;
            const cardClass = `bg-enf-surface-mid border border-enf-surface-border rounded-lg p-4 transition-colors group ${
              hasUrl ? 'hover:border-enf-accent-fg' : 'opacity-90'
            }`;
            const inner = (
              <>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] uppercase tracking-widest text-enf-role-info font-bold">
                    {categoryLabel(g.category)}
                  </span>
                  {hasUrl ? (
                    <ExternalLink className="w-3 h-3 text-enf-text-muted group-hover:text-enf-accent-fg transition-colors flex-shrink-0" />
                  ) : (
                    <span className="text-[9px] uppercase tracking-widest text-enf-text-muted">Coming soon</span>
                  )}
                </div>
                <div className="text-sm font-semibold text-enf-text-primary mb-1">{g.name}</div>
                <div className="text-xs text-enf-text-muted mb-2">{g.priceRange}</div>
                <p className="text-xs text-enf-text-secondary leading-relaxed">{g.blurb}</p>
              </>
            );
            return hasUrl ? (
              <a
                key={g.id}
                href={g.affiliateUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={cardClass}
                data-testid={`adaptive-gear-${g.id}`}
              >
                {inner}
              </a>
            ) : (
              <div key={g.id} className={cardClass} data-testid={`adaptive-gear-${g.id}`}>
                {inner}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GearVault;
